import { getUserId } from "shared-frontend/functions/auth";

import { useGetUpcomingRenewals } from "../api/generated/customer/customer";

/**
 * The rows as they read before the query resolves, on error, and when there is no customer to ask
 * about.
 *
 * Frozen and shared so the identity is stable across renders: the banner returns null on an empty
 * list, and a fresh array each time would re-run its grouping for nothing.
 */
const EMPTY_RENEWALS = Object.freeze( [] );

/**
 * The subscriptions needing a manual renewal within the month, from
 * `GET Customers/:id/subscriptions/upcoming-renewals`.
 *
 * Replaces the `getUpcomingRenewalSubscriptions` selector, which scanned the whole subscription
 * collection — and always returned nothing, because it keyed off a `lastBillingTerm` that never reached
 * the browser. The server reads the billing term from the recurrence rule instead, so this is the first
 * time the banner has anything to render.
 *
 * Rows arrive ordered soonest-expiry-first, so no sorting happens here.
 *
 * Deliberately holds no `useEffect`: this is a read, so there is nothing to mirror into Redux and
 * nothing to report to the data layer. Analytics belong on the mutation that caused an event, never on
 * a query result, which reflects cache state rather than something having happened.
 *
 * @returns {{renewals: Object[], isLoading: boolean, isError: boolean}} The rows and query state.
 */
export default function useUpcomingRenewals() {
	const customerId = getUserId();

	const { data, isLoading, isError } = useGetUpcomingRenewals( customerId, {
		query: {
			// `getUserId()` returns an empty string when there is no access token, and the generated
			// `enabled` only guards null and undefined — so without this the hook would request
			// `Customers//subscriptions/upcoming-renewals`.
			enabled: Boolean( customerId ),
		},
	} );

	return {
		renewals: data ?? EMPTY_RENEWALS,
		isLoading,
		isError,
	};
}
